import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Heart, ArrowLeft, Loader2, Lock, CheckCircle } from 'lucide-react';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, new_password: password }),
      });
      if (res.ok) {
        setSuccess(true);
        setTimeout(() => navigate('/login'), 3000);
      } else {
        const data = await res.json();
        setError(data?.detail || 'Failed to reset password');
      }
    } catch (err) {
      setError(`Connection error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
        <Card className="border-[#1a1a2e]/5 shadow-2xl max-w-md w-full">
          <CardContent className="p-8 text-center">
            <p className="text-[#1a1a2e]/50 mb-4">Invalid or missing reset token.</p>
            <Link to="/forgot-password">
              <Button className="bg-gradient-to-r from-[#7c3aed] to-[#db2777] text-white rounded-full">
                Request new link
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4 py-12">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] bg-purple-100/50 rounded-full blur-[100px]" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <Card className="border-[#1a1a2e]/5 shadow-2xl">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7c3aed] to-[#db2777] flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/20">
                <Heart className="w-6 h-6 text-white" strokeWidth={2} fill="currentColor" />
              </div>
              <h1 className="text-2xl font-bold text-[#1a1a2e]">
                {success ? 'Password reset!' : 'Set new password'}
              </h1>
            </div>

            {success ? (
              <div className="text-center space-y-4">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto" />
                <p className="text-[#1a1a2e]/50">Redirecting to login...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="reset-error">{error}</div>
                )}
                <div>
                  <Label className="text-[#1a1a2e]/70">New password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1a1a2e]/30" />
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 border-[#1a1a2e]/10 focus:border-[#7c3aed] rounded-xl py-6"
                      placeholder="New password" required data-testid="new-password-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-[#1a1a2e]/70">Confirm password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1a1a2e]/30" />
                    <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 border-[#1a1a2e]/10 focus:border-[#7c3aed] rounded-xl py-6"
                      placeholder="Confirm password" required data-testid="confirm-password-input" />
                  </div>
                </div>
                <Button type="submit" disabled={loading}
                  className="w-full bg-gradient-to-r from-[#7c3aed] to-[#db2777] text-white rounded-full py-6 font-medium shadow-lg"
                  data-testid="reset-submit-button">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
